import type { UserPermissions } from "@redotech/account-common/permissions";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { useRequiredContext } from "@redotech/react-util/context";
import { RegistrationRewardType } from "@redotech/redo-return-warranties-common/extended-warranty";
import {
  SETTINGS_FLAG_KEYS,
  type SettingsExperience,
  type SettingsFact,
  type SettingsFlag,
  type SettingsVisibilityFacts,
} from "@redotech/redo-settings-common/settings-visibility/conditions";
import {
  isSettingsPageVisible,
  isSettingsSectionVisible,
  type SettingsPageId,
  type SettingsSectionId,
} from "@redotech/redo-settings-common/settings-visibility/settings-pages";
import { isShopifyEmail } from "@redotech/redo-shopify-common/shopify-email";
import { useContext, useMemo } from "react";
import { PermissionsContext } from "../auth/permissions-context";
import { useShippingNewSettings } from "../shipping-new-settings-context";
import { TeamContext } from "../team";
import { useEnabledCapabilities } from "../use-capability";
import { useIsRedoSuppliesTeam } from "../use-is-redo-supplies-team";
import { useTeamFeatureFlag } from "../use-team-feature-flag";
import { UserContext } from "../user";
import { isRestrictedShippingTeam } from "./restricted-shipping-teams";
import { useReturnsSettingsExperience } from "./use-returns-settings-experience";

export interface SettingsVisibility {
  page: (page: SettingsPageId) => boolean;
  section: (section: SettingsSectionId) => boolean;
}

const NO_PERMISSIONS: ReadonlySet<UserPermissions> = new Set();

/** Everything settings visibility reads, gathered once for the active team and user. */
export function useSettingsVisibilityFacts(): SettingsVisibilityFacts {
  const team = useRequiredContext(TeamContext);
  const user = useContext(UserContext);
  const permissions = useContext(PermissionsContext) ?? NO_PERMISSIONS;
  const capabilities = useEnabledCapabilities();
  const returnsExperience = useReturnsSettingsExperience();
  const shippingNewSettings = useShippingNewSettings();
  const redoSuppliesTeam = useIsRedoSuppliesTeam();
  const imsQuickbooks = useTeamFeatureFlag(SETTINGS_FLAG_KEYS.imsQuickbooks, {
    forceEnabledForRedoUsers: false,
  }).enabled;

  return useMemo(() => {
    const flags = new Set<SettingsFlag>();
    if (imsQuickbooks) {
      flags.add("imsQuickbooks");
    }
    const experiences = new Set<SettingsExperience>();
    if (returnsExperience.useNewUi) {
      experiences.add("returnsNewUi");
    }
    if (!returnsExperience.pending && !returnsExperience.useNewUi) {
      experiences.add("returnsLegacyUi");
    }
    if (shippingNewSettings) {
      experiences.add("shippingNewSettings");
    }
    const settings = team.settings;
    const teamFacts: Record<SettingsFact, boolean> = {
      redoSuppliesTeam,
      restrictedShippingTeam: isRestrictedShippingTeam(team._id.toString()),
      shopifyEmailTeam: isShopifyEmail(team.email),
      supportAiSetting: !!settings.support?.ai?.enabled,
      warrantyRegistrationRewards:
        settings.warranties?.registrationRewardType !==
        RegistrationRewardType.DISABLED,
      wmsSourceOfTruth:
        settings.outboundLabels?.wms?.sourceOfTruthEnabled === true,
    };
    const facts = new Set<SettingsFact>();
    for (const [name, value] of Object.entries(teamFacts)) {
      if (value && isSettingsFact(name)) {
        facts.add(name);
      }
    }
    return {
      capabilities,
      permissions,
      flags,
      experiences,
      facts,
      teamType: team.teamType ?? null,
      platform: team.platform ?? null,
      staff: isRedoEmailAddress(user?.email),
    };
  }, [
    team,
    user,
    permissions,
    capabilities,
    returnsExperience.pending,
    returnsExperience.useNewUi,
    shippingNewSettings,
    redoSuppliesTeam,
    imsQuickbooks,
  ]);
}

const SETTINGS_FACT_NAMES: Record<SettingsFact, true> = {
  redoSuppliesTeam: true,
  restrictedShippingTeam: true,
  shopifyEmailTeam: true,
  supportAiSetting: true,
  warrantyRegistrationRewards: true,
  wmsSourceOfTruth: true,
};

function isSettingsFact(name: string): name is SettingsFact {
  return Object.hasOwn(SETTINGS_FACT_NAMES, name);
}

/** The one way settings UI decides what shows: pages, sections, routes and tabs all ask this. */
export function useSettingsVisibility(): SettingsVisibility {
  const facts = useSettingsVisibilityFacts();
  return useMemo(
    () => ({
      page: (page) => isSettingsPageVisible(page, facts),
      section: (section) => isSettingsSectionVisible(section, facts),
    }),
    [facts],
  );
}
